import classNames from 'classnames';
import { useMemo } from 'react';
import { RenderSettingsParams } from '../UserSettingsForm/RenderSettings';
import { ReactComponent as LockImage } from '../../../../images/lock.svg';
import { dataTestId } from './common';
import styles from './UserSettingsAndAccessForm.module.css';
import { shouldRender } from 'admin/utils/shouldRender';
import { UserOrganizationSettings, UserSettings } from 'admin/types';

type SettingGroup = {
  isPersonaForm?: boolean;
  settings: RenderSettingsParams[];
  title: string;
  userOrgSettings: UserOrganizationSettings;
};

export const isSettingVisible = (
  {
    requiredOrgSettings,
    requiredIndividualCondition,
    forbiddenOffCondition,
    show = true,
    hideFromPersonaForm = false,
  }: RenderSettingsParams,
  userOrgSettings: UserOrganizationSettings,
  isPersonaForm = false
) =>
  !!userOrgSettings &&
  show &&
  !(isPersonaForm && hideFromPersonaForm) &&
  shouldRender({
    orgSettings: userOrgSettings,
    requiredOrgSettings,
    requiredIndividualCondition,
    forbiddenOffCondition,
  });

export const visibleSettingKeys = (
  settings: RenderSettingsParams[],
  userOrgSettings: UserOrganizationSettings,
  isPersonaForm = false
): Array<keyof UserSettings> =>
  settings
    .filter((setting) => isSettingVisible(setting, userOrgSettings, isPersonaForm))
    .flatMap(({ userSettingKey }) => (userSettingKey ? [userSettingKey] : []));

function SettingsGroup({ isPersonaForm, settings, title, userOrgSettings }: SettingGroup) {
  const settingsToRender = useMemo(
    () => settings.filter((setting) => isSettingVisible(setting, userOrgSettings, isPersonaForm)),
    [isPersonaForm, settings, userOrgSettings]
  );

  if (!settingsToRender.length) return undefined;

  return (
    <>
      <div className={styles.settingsGroupTitle}>{title}</div>
      <div className={styles.settingsGroupGrid}>
        {settingsToRender.map((setting) => (
          <div
            className={classNames(styles.settingsGroupGridItem, { [styles.span]: setting.span })}
            key={setting.label}
          >
            <div className={styles.container} data-testid={dataTestId('setting', setting.label)}>
              <div className={styles.left}>
                <div className={styles.title}>{setting.label}</div>
                {setting.lockDesc && (
                  <div className={styles.lockDesc}>
                    <LockImage />
                    {setting.lockDesc}
                  </div>
                )}
                {setting.optInControl && isPersonaForm && (
                  <div className={styles.personaOptInControl}>{setting.optInControl}</div>
                )}
                <div className={styles.description}>{setting.subLabel}</div>
              </div>
              <div className={styles.right}>
                {isPersonaForm && setting.personaSettingControl
                  ? setting.personaSettingControl
                  : setting.settingControl}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default SettingsGroup;
